/* =====================================================================
   AstaLab 2.0 — providerEnrichmentEngine.js (round alpha.3, fondamenta enrichment provider-neutral
   — vedi PROVIDER_ENRICHMENT_DESIGN_alpha3_r1.md)

   Motore PURO (nessun I/O, nessuna rete, nessuna API key) che converte un'osservazione RAW nella
   forma proprietaria di un adapter provider (frontend/src/providers/*Adapter.js) nella forma
   NORMALIZZATA condivisa già usata da externalRecordsEngine.js — la stessa identica forma prodotta
   da normalizeExternalStatisticsRecord() per una riga CSV, con l'aggiunta dei quattro campi
   OPZIONALI di identità/versione provider (providerKey/providerObservationId/providerObservedAt/
   providerPayloadChecksum — vedi 0010_provider_observation_versioning.sql).

   Separazione esplicita (obiettivo del round): QUESTO file non sa nulla del trasporto/formato di un
   provider specifico — riceve un oggetto già "raw provider shape" e produce SEMPRE la stessa forma
   AstaLab in uscita. Un adapter concreto (frontend/src/providers/*Adapter.js) è l'UNICO punto che
   conosce la forma proprietaria di un provider; un futuro provider reale userà questo stesso motore
   senza alcuna modifica, cambiando solo l'adapter che lo alimenta.

   Disciplina fail-closed (stesso principio di externalRecordsEngine.js — revisione r4, blocker 2,
   "normalizeExternalStatisticsRecord() NON deve più essere il cancello che decide se un import è
   valido, perché CLAMPA invece di rifiutare"): la validazione dei campi NUMERICI avviene QUI su
   valori RAW, PRIMA di normalizeExternalStatisticsRecord() — mai dopo, altrimenti un valore fuori
   range verrebbe silenziosamente clampato dal motore condiviso prima ancora di poter essere
   rifiutato.

   Revisione alpha.3-r2 (audit blocker 3 su alpha.3-r1): due provider autorizzati diversi condividono
   sempre `source="provider-authorized"` (un livello di fiducia, mai un'identità di vendor) — questo
   file impone quindi una garanzia aggiuntiva, verificata PRIMA che un'osservazione possa raggiungere
   il motore di riconciliazione: un'osservazione non può dichiararsi "versioned" con un contratto
   incompleto — providerKey/providerObservationId/providerObservedAt devono essere forniti TUTTI E
   TRE insieme, mai un sottoinsieme (un providerObservationId senza providerObservedAt, per esempio,
   potrebbe altrimenti bypassare silenziosamente la protezione da staleness del fold — vedi
   externalRecordsEngine.js).

   Revisione alpha.3-r4 (terzo audit indipendente, obiettivo architetturale): PRIMA di questa
   revisione, `sourcePlayerId` di un'osservazione provider-authorized era il canonico
   "providerKey:idGrezzo" (buildVendorScopedSourcePlayerId, r2) — un doppio encoding del vendor,
   perché `external_player_ids`/`external_statistics` hanno GIÀ una colonna `provider_key` dedicata
   (blocker B, r3). Il modello ora è quello SEPARATO: `source_player_id`/`sourcePlayerId` è SEMPRE
   l'id grezzo del provider, MAI prefissato — l'identità vendor vive SOLO nella colonna/campo
   `provider_key`, mai codificata due volte nella stessa stringa. La disambiguazione fra due vendor
   che condividono lo stesso id grezzo (es. entrambi "123") è ora responsabilità del CONFRONTO
   (provider_key + source_player_id insieme, uguaglianza ESATTA — mai una LIKE/wildcard, vedi
   externalRecordsEngine.resolveExternalRecordPlayer() e private.reconcile_external_records() in
   0010), non più della rappresentazione della stringa stessa. `buildVendorScopedSourcePlayerId()` è
   stata rimossa: nessun punto di questo file costruisce più un id composito.
   ===================================================================== */
import { EXTERNAL_STATISTICS_RANGES, EXTERNAL_SOURCE_WHITELIST, normalizeExternalStatisticsRecord } from "./externalRecordsEngine.js";
import { parseSeasonKey } from "./statisticsEngine.js";

const PROVIDER_SOURCE = "provider-authorized";

// Corrispondenza fra i nomi RAW proprietari di un adapter (forma "provider", vedi
// MockFixtureProviderAdapter.js) e le chiavi AstaLab/EXTERNAL_STATISTICS_RANGES — un futuro adapter
// per un provider reale usa la STESSA mappa, mai una copia.
const RAW_NUMERIC_FIELD_MAP = {
  matchesPlayed: "appearances", matchesStarted: "starts", minutesPlayed: "minutes",
  goalsScored: "goals", assistsMade: "assists", yellowCardsShown: "yellowCards",
  redCardsShown: "redCards", penaltyGoals: "penaltiesScored", penaltyMisses: "penaltiesMissed",
  cleanSheetsKept: "cleanSheets", goalsConcededTotal: "goalsConceded"
};

function isBlank(v){ return v==null || String(v).trim()===""; }
function toStrOrEmpty(v){ return v==null ? "" : String(v).trim(); }

/** Formato di `providerKey`: lunghezza <=200 (coerente col CHECK di tabella in 0010). `null`/assente
 *  è legittimo (osservazione non versioned). Revisione alpha.3-r4: NON vieta più ':' — quel divieto
 *  esisteva solo perché ':' era il separatore di namespace usato per costruire sourcePlayerId
 *  (buildVendorScopedSourcePlayerId, r2/r3, rimossa in questo round — vedi commento di testa del
 *  file). Un providerKey può ora legittimamente contenere QUALUNQUE carattere, incluso '%'/'_' (i
 *  metacaratteri di SQL LIKE) — non hanno più alcun ruolo speciale, dato che ogni confronto di
 *  identità in questo round usa uguaglianza ESATTA, mai una LIKE (blocker 4, vedi
 *  private.reconcile_external_records()/associate_external_id() in 0010). */
export function validateProviderKeyFormat(providerKey){
  if(providerKey==null) return null;
  const s = String(providerKey);
  if(s.trim()==="") return "providerKey non può essere una stringa vuota/di soli spazi.";
  if(s.length>200) return "providerKey troppo lungo (limite 200 caratteri).";
  return null;
}

/** Valida STRICT i campi NUMERICI di un'osservazione RAW (prima di qualunque mappatura/clamp) —
 *  mai un clamp silenzioso: un valore presente ma non intero o fuori range rifiuta l'INTERA
 *  osservazione. Un campo assente/null resta legittimamente assente (mai un errore). */
export function validateRawProviderObservationNumbers(raw, index){
  const label = "Osservazione " + (index!=null ? index+1 : "?");
  const src = raw && typeof raw==="object" ? raw : {};
  for(const [rawField, rangeKey] of Object.entries(RAW_NUMERIC_FIELD_MAP)){
    const v = src[rawField];
    if(v==null || v==="") continue;
    const n = typeof v==="number" ? v : Number(v);
    const range = EXTERNAL_STATISTICS_RANGES[rangeKey];
    if(!Number.isFinite(n) || n!==Math.floor(n)){
      return label + ": " + rawField + " deve essere un intero (\""+v+"\").";
    }
    if(n<range[0] || n>range[1]){
      return label + ": " + rawField + " fuori range (" + range[0] + ".." + range[1] + "): " + n + ".";
    }
  }
  return null;
}

/** Converte UNA osservazione raw (forma proprietaria dell'adapter) nella riga normalizzata AstaLab
 *  (stessa forma di normalizeExternalStatisticsRecord()), pronta per la coda
 *  unassociatedExternalStatistics. Revisione alpha.3-r4: `sourcePlayerId` è ora SEMPRE l'id grezzo
 *  del provider, trimmato ma MAI prefissato/namespacizzato — l'identità vendor vive solo nel campo
 *  `providerKey`, separato (vedi commento di testa del file). Non valida i campi numerici: chiamare
 *  SEMPRE validateRawProviderObservationNumbers() sul valore RAW prima di questa funzione (vedi
 *  mapAndValidateProviderObservations, che applica l'ordine corretto). */
/** Canonicalizza `providerKey` (trim, mai altro) — UNICO punto in cui un providerKey grezzo diventa
 *  il valore usato ovunque nella pipeline (blocker C, r3, ancora valido in questo round): " vendor-a "
 *  e "vendor-a" devono produrre lo STESSO valore, sempre e ovunque, mai un trim ad-hoc locale. */
export function canonicalizeProviderKey(providerKey){
  if(providerKey==null) return null;
  const s = String(providerKey).trim();
  return s===""? "" : s; // stringa vuota/di soli spazi -> "" (validateProviderKeyFormat la rifiuta esplicitamente, mai un null silenzioso che la farebbe passare come "non versioned")
}

export function mapProviderObservationToExternalStatisticsRow(raw, providerKey){
  const src = raw && typeof raw==="object" ? raw : {};
  const ref = src.playerRef && typeof src.playerRef==="object" ? src.playerRef : {};
  const canonicalProviderKey = providerKey==null ? null : (canonicalizeProviderKey(providerKey) || null);
  return normalizeExternalStatisticsRecord({
    fantacalcioId: ref.fantacalcioId!=null ? ref.fantacalcioId : null,
    sourcePlayerId: toStrOrEmpty(ref.externalRef),
    nome: src.playerName!=null ? src.playerName : "",
    season: src.season,
    source: PROVIDER_SOURCE,
    sourceUrl: src.sourceUrl!=null ? src.sourceUrl : "",
    team: src.club!=null ? src.club : "",
    competition: src.competition!=null ? src.competition : "",
    appearances: src.matchesPlayed,
    starts: src.matchesStarted,
    minutes: src.minutesPlayed,
    goals: src.goalsScored,
    assists: src.assistsMade,
    yellowCards: src.yellowCardsShown,
    redCards: src.redCardsShown,
    penaltiesScored: src.penaltyGoals,
    penaltiesMissed: src.penaltyMisses,
    cleanSheets: src.cleanSheetsKept,
    goalsConceded: src.goalsConcededTotal,
    notes: src.note!=null ? src.note : "",
    providerKey: canonicalProviderKey,
    providerObservationId: src.obsId,
    providerObservedAt: src.obsTime,
    providerPayloadChecksum: src.checksum
  });
}

/** Valida STRICT i campi NON numerici di un'osservazione già mappata (identificabilità, season,
 *  source, formato/lunghezza dei metadati provider, contratto "versioned" fail-closed) — i campi
 *  numerici sono già stati validati sul valore RAW da validateRawProviderObservationNumbers() prima
 *  della mappatura. */
export function validateProviderObservation(mapped, index){
  const label = "Osservazione " + (index!=null ? index+1 : "?");
  if(isBlank(mapped.fantacalcioId) && isBlank(mapped.sourcePlayerId)){
    return label + ": né fantacalcioId né sourcePlayerId sono presenti (osservazione non identificabile).";
  }
  if(isBlank(mapped.season) || !parseSeasonKey(String(mapped.season).trim())){
    return label + ": season non valida o mancante (\""+mapped.season+"\").";
  }
  if(!EXTERNAL_SOURCE_WHITELIST.includes(mapped.source)){
    return label + ": source non consentita (\""+mapped.source+"\").";
  }
  if(!isBlank(mapped.providerObservedAt) && Number.isNaN(Date.parse(mapped.providerObservedAt))){
    return label + ": providerObservedAt non è un timestamp valido (\""+mapped.providerObservedAt+"\").";
  }
  const providerKeyErr = validateProviderKeyFormat(isBlank(mapped.providerKey) ? null : mapped.providerKey);
  if(providerKeyErr) return label + ": " + providerKeyErr;
  if(!isBlank(mapped.providerObservationId) && String(mapped.providerObservationId).length>200){
    return label + ": providerObservationId troppo lungo (limite 200 caratteri).";
  }
  if(!isBlank(mapped.providerPayloadChecksum) && String(mapped.providerPayloadChecksum).length>128){
    return label + ": providerPayloadChecksum troppo lungo (limite 128 caratteri).";
  }
  // Revisione alpha.3-r2 (audit blocker 3, contratto "versioned" fail-closed): providerKey/
  // providerObservationId/providerObservedAt devono essere forniti TUTTI E TRE insieme o NESSUNO —
  // un'osservazione non può dichiararsi versioned con un'identità/timestamp incompleti. Senza questo
  // controllo, un'osservazione con SOLO providerObservationId (senza providerObservedAt) potrebbe
  // raggiungere il fold e — a seconda di come un chiamante futuro la usasse — bypassare
  // silenziosamente la protezione da staleness, che dipende da un istante reale su ENTRAMBI i lati.
  const hasKey = !isBlank(mapped.providerKey), hasObsId = !isBlank(mapped.providerObservationId), hasObsAt = !isBlank(mapped.providerObservedAt);
  const anyProvided = hasKey || hasObsId || hasObsAt;
  const allProvided = hasKey && hasObsId && hasObsAt;
  if(anyProvided && !allProvided){
    return label + ": providerKey/providerObservationId/providerObservedAt devono essere forniti TUTTI E TRE insieme o NESSUNO — un'osservazione non può dichiararsi versioned con un'identità/timestamp incompleti (fail-closed).";
  }
  return null;
}

/** Pipeline completa: valida (raw, poi mappato) + mappa un array di osservazioni raw di UN adapter
 *  provider. Ritorna SEMPRE {ok, error, records}: se anche una sola osservazione è invalida, ok è
 *  false, error descrive ESATTAMENTE quale, records è vuoto — mai un risultato parziale (stesso
 *  contratto di parseAndValidateExternalStatisticsCSV()). `providerKey` identifica QUALE adapter/
 *  provider ha prodotto queste osservazioni — mai il valore di `source`, che resta il livello di
 *  fiducia fisso ("provider-authorized"), non l'identità del vendor. Il formato di `providerKey`
 *  viene validato E canonicalizzato UNA VOLTA qui (è uniforme per l'intero batch) PRIMA di mappare
 *  qualunque riga, cosi' che ogni riga mappata di questo batch riceva lo STESSO valore canonico
 *  (blocker C). */
export function mapAndValidateProviderObservations(rawObservations, providerKey){
  if(!Array.isArray(rawObservations) || rawObservations.length===0){
    return { ok:false, error:"Nessuna osservazione da importare.", records:[] };
  }
  const providerKeyErr = validateProviderKeyFormat(providerKey);
  if(providerKeyErr) return { ok:false, error:providerKeyErr, records:[] };
  // Revisione alpha.3-r3 (audit blocker C su r2): canonicalizzato (trim) UNA SOLA VOLTA qui — poi
  // SEMPRE il valore canonico, mai più il valore grezzo, in ogni riga mappata di questo batch
  // (mapProviderObservationToExternalStatisticsRow canonicalizza comunque anche al proprio interno,
  // in difesa, ma il valore che questa pipeline usa/passa è già quello canonico).
  const canonicalProviderKey = canonicalizeProviderKey(providerKey);
  const records = [];
  for(let i=0; i<rawObservations.length; i++){
    const rawErr = validateRawProviderObservationNumbers(rawObservations[i], i);
    if(rawErr) return { ok:false, error:rawErr, records:[] };
    const mapped = mapProviderObservationToExternalStatisticsRow(rawObservations[i], canonicalProviderKey);
    const err = validateProviderObservation(mapped, i);
    if(err) return { ok:false, error:err, records:[] };
    records.push(mapped);
  }
  return { ok:true, error:null, records };
}

export { PROVIDER_SOURCE };
